import { useAction, useAtom } from '@reatom/npm-react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { Redacted } from '@blade/std-lib'
import { Button } from '@blade/ui-kit'
import { isPasswordValid } from '@root/entities/auth/constants'
import { InputPassword, mediaMaxTablet } from '@root/shared/ui-kit'

import { UpdatePasswordHeading } from './update-password-heading'

import type { LocalPasswordEditFlowReatom } from '../atoms/update-local-password'
import type { PasswordEditFlowReatom } from '../atoms/update-password'

type Props = { model: PasswordEditFlowReatom | LocalPasswordEditFlowReatom }

export const UpdatePasswordUpdate: React.FC<Props> = ({ model }) => {
  const { t } = useTranslation()

  const [stage] = useAtom(model.stageAtom)
  const [requestState] = useAtom(model.updatePasswordStateAtom)

  const setNewPassword = useAction(model.setNewPasswordAction)
  const confirmNewPassword = useAction(model.confirmNewPasswordAction)

  const { formState, control, handleSubmit, reset } = useForm({
    defaultValues: {
      password: '',
    },
    mode: 'all',
  })

  const handleSubmitInternal = handleSubmit((values) => {
    if (stage.type === 'new-password') {
      setNewPassword(Redacted.make(values.password))
      reset()
      return
    }
    confirmNewPassword(Redacted.make(values.password))
  })

  const validatePassword = (password: string) => {
    const isValid = isPasswordValid(password)

    if (!isValid) {
      return false
    }

    // eslint-disable-next-line sonarjs/prefer-single-boolean-return
    if (
      stage.type === 'repeat' &&
      Redacted.value(stage.newPassword) !== password
    ) {
      return false
    }

    return true
  }

  return (
    <Root>
      <UpdatePasswordHeading
        title={
          stage.type === 'new-password'
            ? t('settings.password.title-new')
            : t('settings.password.title-new-repeat')
        }
      />

      <Form noValidate onSubmit={handleSubmitInternal}>
        <Controller
          name="password"
          control={control}
          rules={{
            validate: {
              validatePassword,
            },
          }}
          render={({ field }) => (
            <InputPasswordConfirm
              {...field}
              autoCorrect="off"
              spellCheck={false}
              autoCapitalize="off"
              autoComplete="off"
              size="m"
            />
          )}
        />
        <ButtonContinue
          disabled={!formState.isValid}
          busy={requestState.loading}
          variant="soft"
          size="xs"
          type="submit"
          radius="rounded"
        >
          {t('common.continue')}
        </ButtonContinue>
      </Form>
    </Root>
  )
}

const Root = styled.div`
  height: 100%;
  display: grid;
  grid-template-rows: auto 1fr;
`

const Form = styled.form`
  display: grid;
  grid-template-rows: 1fr auto;
  gap: 20px;
`

const ButtonContinue = styled(Button)`
  place-self: center;
`

const InputPasswordConfirm = styled(InputPassword)`
  place-self: center;

  ${mediaMaxTablet(css`
    place-self: unset;
    padding-inline: 20px;
  `)}
`
